import { useState, useEffect } from "react"; // Adicionado useEffect
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, ArrowLeft, Check, Sparkles, Calendar
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// Questions configuration
const questions = [
  {
    id: "perfil_financeiro",
    question: "Como você se descreve com dinheiro?",
    type: "options",
    options: [
      { value: "organizado", label: "Organizado", emoji: "📊" },
      { value: "tento", label: "Eu tento", emoji: "🤞" },
      { value: "improviso", label: "Improviso", emoji: "🎲" },
      { value: "nao_sei", label: "Não sei onde meu salário vai", emoji: "🫠" },
    ],
  },
  {
    id: "divisao_contas",
    question: "Com quem você costuma dividir as contas?",
    type: "options",
    options: [
      { value: "parceiro", label: "Parceiro(a)", emoji: "💑" },
      { value: "familia", label: "Família", emoji: "👨‍👩‍👧‍👦" },
      { value: "amigos", label: "Amigos", emoji: "👯" },
      { value: "ninguem", label: "Ninguém", emoji: "🧘" },
      { value: "depende", label: "Depende do mês", emoji: "🔄" },
      { value: "outros", label: "Outros", emoji: "🤷" },
    ],
  },
  {
    id: "meta_momento",
    question: "Qual é sua meta do momento?",
    type: "options",
    options: [
      { value: "viajar", label: "Viajar", emoji: "✈️" },
      { value: "quitar_comprar", label: "Quitar/comprar algo", emoji: "🛒" },
      { value: "reserva", label: "Fazer reserva", emoji: "🏦" },
      { value: "sobreviver", label: "Sobreviver ao mês", emoji: "😅" },
      { value: "todas", label: "Todas as opções acima", emoji: "🎯" },
    ],
  },
  {
    id: "data_nascimento",
    question: "Quando você nasceu?",
    type: "date_input", // Mudamos o tipo para nossa lógica customizada
  },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { completeOnboarding } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateDisplay, setDateDisplay] = useState(""); // Estado para a máscara DD/MM/AAAA
  const [dateMessage, setDateMessage] = useState("Precisamos saber se seu ascendente é em boleto ou em investimentos. 💸");

  const [answers, setAnswers] = useState({
    perfil_financeiro: "",
    divisao_contas: "",
    meta_momento: "",
    data_nascimento: null,
  });

  const currentQuestion = questions[currentStep];
  const totalSteps = questions.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Lógica de Máscara e Validação de Data
  const handleDateInput = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove tudo que não é número
    
    if (value.length > 8) value = value.slice(0, 8);

    // Aplica a máscara DD/MM/AAAA
    if (value.length >= 5) {
      value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
    } else if (value.length >= 3) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`;
    }

    setDateDisplay(value);

    // Só valida e atualiza o estado real quando a data estiver completa
    if (value.length === 10) {
      const [day, month, year] = value.split('/').map(Number);
      const currentYear = new Date().getFullYear();
      
      // Regras de Segurança
      if (day > 31 || day < 1 || month > 12 || month < 1) {
        toast.error("Data inválida!");
        return;
      }
      if (year < currentYear - 100) {
        toast.error("Você é um vampiro? 🧛‍♂️ (Ano muito antigo)");
        return;
      }
      if (year > currentYear) {
        toast.error("Você veio do futuro? 🚀");
        return;
      }

      // Mensagem divertida baseada no ano
      if (year <= 1995) {
        setDateMessage("Experiente e Organizado! 😎");
      } else {
        setDateMessage("Jovem e organizado! ✨");
      }

      // Formata para o Firebase (YYYY-MM-DD)
      const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      setAnswers({ ...answers, data_nascimento: formattedDate });
    } else {
      setAnswers({ ...answers, data_nascimento: null });
    }
  };

  const handleOptionSelect = (value) => {
    setAnswers({ ...answers, [currentQuestion.id]: value });
  };

  const isCurrentStepValid = () => {
    const answer = answers[currentQuestion.id];
    return answer !== "" && answer !== null;
  };

  const handleNext = () => {
    if (!isCurrentStepValid()) {
      toast.error(currentQuestion.id === "data_nascimento" ? "Insira uma data válida!" : "Por favor, selecione uma opção!");
      return;
    }
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    
    // Garantimos que estamos enviando o sinal de que o onboarding acabou
    const onboardingData = {
      ...answers,
      onboardingComplete: true, // Adicione isso para o seu AuthContext saber
      completedAt: new Date().toISOString(),
      // Caso seu AuthContext não faça isso, adicione os campos padrão aqui:
      plan: "trial",
      planStatus: "active",
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 dias
    };
    
    const result = await completeOnboarding(onboardingData);
    setIsSubmitting(false);
    
    if (result.success) {
      toast.success("Tudo pronto! Vamos começar! 🎉");
      // Pequeno delay para o Firebase processar a nova regra de sessão
      setTimeout(() => navigate("/home"), 500);
    } else {
      // DICA: Imprima o erro no console para saber exatamente o que o Firebase disse
      console.error("Erro no Onboarding:", result.error); 
      toast.error("Ops! Algo deu errado ao salvar seus dados.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-lg mx-auto px-6 py-8">
        
        {/* Header Progress */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-slate-500">{currentStep + 1} de {totalSteps}</span>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              <span className="text-sm font-medium text-indigo-500">Troquinho</span>
            </div>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <motion.div
              animate={{ width: `${progress}%` }}
              className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full"
            />
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="mb-8"
          >
            <h1 className="text-2xl font-black tracking-tight text-slate-900 mb-4 text-center">
              {currentQuestion.question}
            </h1>

            {/* Mensagem Dinâmica */}
            <p className="text-center text-slate-500 mb-8 italic min-h-[40px]">
              "{currentQuestion.id === "data_nascimento" ? dateMessage : "Vamos personalizar sua experiência."}"
            </p>

            {/* Renderização das Opções */}
            {currentQuestion.type === "options" && (
              <div className={`grid gap-3 ${currentQuestion.options.length > 4 ? "grid-cols-2" : "grid-cols-1"}`}>
                {currentQuestion.options.map((option, index) => (
                  <motion.button
                    key={option.value}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleOptionSelect(option.value)}
                    className={`relative p-4 rounded-[20px] border-2 transition-all ${
                      answers[currentQuestion.id] === option.value
                        ? "border-indigo-500 bg-indigo-50 shadow-lg shadow-indigo-500/10"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{option.emoji}</span>
                      <span className={`font-semibold ${answers[currentQuestion.id] === option.value ? "text-indigo-700" : "text-slate-700"}`}>
                        {option.label}
                      </span>
                    </div>
                  </motion.button>
                ))}
              </div>
            )}

            {/* Novo Input de Data Customizado */}
            {currentQuestion.id === "data_nascimento" && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-sm mx-auto">
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric" // Abre teclado numérico no celular
                    placeholder="DD/MM/AAAA"
                    value={dateDisplay}
                    onChange={handleDateInput}
                      autoComplete="off" // Impede que o iPhone sugira datas do catálogo de endereços
                      autoCorrect="off"  // Desativa correção automática
                      spellCheck="false" // Desativa verificação ortográfica
                      name="data-nascimento-custom" // Um nome genérico evita que o iOS reconheça como campo padrão de sistema
                      className="w-full h-20 text-center text-2xl font-bold rounded-[25px] border-4 border-slate-200 bg-white text-slate-900 focus:border-indigo-500 focus:ring-0 outline-none transition-all placeholder:text-slate-300"
                    />
                  <div className="absolute top-1/2 left-5 -translate-y-1/2 text-slate-300">
                    <Calendar className="w-6 h-6" />
                  </div>
                </div>
                <p className="mt-4 text-center text-sm text-slate-400">
                  Digite os números da sua data de nascimento
                </p>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Botões de Navegação */}
        <div className="flex gap-3">
          {currentStep > 0 && (
            <Button variant="outline" onClick={handleBack} className="flex-1 h-14 rounded-full font-bold border-2">
              <ArrowLeft className="w-5 h-5 mr-2" /> Voltar
            </Button>
          )}
          <Button
            onClick={handleNext}
            disabled={!isCurrentStepValid() || isSubmitting}
            className={`h-14 rounded-full font-bold bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-lg ${currentStep === 0 ? "w-full" : "flex-1"} ${(!isCurrentStepValid() || isSubmitting) ? "opacity-50" : ""}`}
          >
            {isSubmitting ? "Salvando..." : currentStep === totalSteps - 1 ? "Começar! ✨" : "Próximo"}
          </Button>
        </div>
      
      </div>
    </div>
  );
}